import { Framework } from '@/framework/instance';
import { ILayerProps } from '@/version-types';
import { Collaborators } from './core/collaborators';
import { CustomMessage } from './core/operate/custom-message';
import { CustomOperate } from './core/operate/custom-operate';
import { MoveCursor } from './core/operate/move-cursor';
import { MoveOperate } from './core/operate/move-operate';
import { PickLayer } from './core/operate/pick-layer';
import { ResizeOperate } from './core/operate/resize-operate';
import { RotateOperate } from './core/operate/rotate-operate';
import { ScaleOperate } from './core/operate/scale-operate';
import { StageUnit } from './core/operate/stage-unit';
import { UserSave } from './core/operate/user-save';
import { Room } from './core/room';
import { IRealEditConfig } from './types';
import { OperateType } from './types/operate';

/**
 * CRDT (Conflict-free Replicated Data Type)
 * CRDT 是一种通过数据结构设计来避免冲突的算法：
 * 实时编辑
 */
export class RealEdit extends Framework {
    /** 房间 */
    room: Room;
    /** 协作成员 */
    collaborators: Collaborators;
    /** 选中图层操作 */
    pickLayer: PickLayer;
    /** 移动光标 */
    moveCursor: MoveCursor;
    /** 移动操作 */
    moveOperate: MoveOperate;
    /** 旋转操作 */
    rotateOperate: RotateOperate;
    /** 缩放操作 */
    scaleOperate: ScaleOperate;
    /** 尺寸调整操作 */
    resizeOperate: ResizeOperate;
    /** 自定义操作 */
    customOperate: CustomOperate;
    /** 用户保存 */
    userSave: UserSave;
    /** 舞台单位 */
    stageUnit: StageUnit;
    /** 自定义事件 */
    customMessage: CustomMessage;

    constructor(identity: string) {
        super(identity);
        // console.log('real-edit', this.identity);
        this.collaborators = new Collaborators();
        this.room = new Room(this.identity, this.collaborators, {
            syncUpdateAt: (updateAt: number) => {
                this.userSave.setUpdateAt(updateAt);
            },
        });
        this.pickLayer = new PickLayer(this.room, this.collaborators);
        this.moveCursor = new MoveCursor(this.identity, this.room, this.collaborators);
        this.moveOperate = new MoveOperate(this.identity, this.room, this.collaborators);
        this.rotateOperate = new RotateOperate(this.identity, this.room, this.collaborators);
        this.scaleOperate = new ScaleOperate(this.identity, this.room, this.collaborators);
        this.resizeOperate = new ResizeOperate(this.identity, this.room, this.collaborators);
        this.customOperate = new CustomOperate(this.identity, this.room, this.collaborators);
        this.userSave = new UserSave(this.identity, this.room, this.collaborators);
        this.stageUnit = new StageUnit(this.identity, this.room, this.collaborators);
        this.customMessage = new CustomMessage(this.identity, this.room);
    }

    /** 设置配置 */
    setConfig(config: IRealEditConfig) {
        this.room.setConfig(config);
    }

    /** 是否是多人协同编辑 */
    isRealEdit() {
        return this.room.isRealEdit();
    }

    /** 同步动态背景信息 */
    syncDynamicBgInfo(data?: any) {
        this.customMessage.sendToAll({
            _: OperateType.SyncDynamicBgInfo,
            d: data,
        });
    }

    /** 监听动态背景信息 */
    onDynamicBgInfo(callback: (data: any) => void) {
        return this.customMessage.onToAllMessage((message) => {
            if (message._ === OperateType.SyncDynamicBgInfo) {
                callback(message.d);
            }
        });
    }

    /**
     * 发送图层数据给其他用户
     */
    sendLayer(layer: ILayerProps) {
        this.framework.history.realEdit.sendLayer(layer);
    }

    /**
     * 发送差异数据给其他用户
     */
    sendDiff(data: any) {
        this.framework.history.realEdit.sendDiff(data);
    }

    /**
     * 发送历史记录步骤
     * @param type - 历史记录类型
     * @param data - 数据
     */
    send(type: string, data: any) {
        this.framework.history.realEdit.send(type, data);
    }
}
